import type { AnalyticsExportFile } from '@/lib/analytics-export'
import type { AnalyticsExportRequestRow } from '@/lib/analytics-export-request'
import type { AnalyticsExportResponsePreviewDownloadMode } from '@/lib/analytics-export-response-preview'

export type AnalyticsExportDownloadStateReadyState =
  | 'ready_for_browser_download'
  | 'signed_url_pending'
  | 'not_ready'

export type AnalyticsExportDownloadStateRetentionPolicy =
  | 'not_persisted_mock_safe'
  | 'pending_export_job_retention'

export type AnalyticsExportDownloadStateFileMetadata = Pick<
  AnalyticsExportFile,
  'fileName' | 'mimeType' | 'rowCount'
>

export type AnalyticsExportDownloadStatePreviewInput = {
  file: AnalyticsExportDownloadStateFileMetadata | null
  downloadMode: AnalyticsExportResponsePreviewDownloadMode
  readyState: AnalyticsExportDownloadStateReadyState
  providerState: string
  retentionPolicy: AnalyticsExportDownloadStateRetentionPolicy
}

export type AnalyticsExportDownloadStatePreviewPayload = {
  downloadMode: AnalyticsExportResponsePreviewDownloadMode
  fileName: string
  mimeType: string
  rowCount: number
  readyState: AnalyticsExportDownloadStateReadyState
  providerState: string
  retentionPolicy: AnalyticsExportDownloadStateRetentionPolicy
}

export type AnalyticsExportDownloadStatePreview = {
  payload: AnalyticsExportDownloadStatePreviewPayload
  rows: AnalyticsExportRequestRow[]
  mockSafe: true
}

const forbiddenDownloadStatePreviewKeys = new Set([
  'answer',
  'answers',
  'rawanswer',
  'rawanswers',
  'sample',
  'samples',
  'rawsample',
  'rawsamples',
  'content',
  'row',
  'rows',
  'rawrow',
  'rawrows',
  'exportrow',
  'exportrows',
  'email',
  'contactemail',
  'phone',
  'contactphone',
  'mobile',
  'address',
  'companyaddress',
  'taxid',
  'tax_id',
  'companytaxid',
  'businessaccountingno',
  'providerpayload',
  'd1rows',
  'rawd1rows',
])

const unsafeDownloadStateValuePatterns = [
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
  /09\d{8}/,
  /0\d{1,2}-?\d{6,8}(?:#?\d+)?/,
  /[\u4e00-\u9fff]{0,8}(市|縣).{0,24}(路|街|巷|弄|號)/,
]

const unsafeDownloadStateFileNameValuePatterns = [
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i,
  /(^|[^0-9])09\d{8}([^0-9]|$)/,
  /(^|[^0-9])0\d{1,2}-\d{6,8}(?:#?\d+)?([^0-9]|$)/,
  /[\u4e00-\u9fff]{0,8}(市|縣).{0,24}(路|街|巷|弄|號)/,
]

export function buildAnalyticsExportDownloadStatePreview(
  input: AnalyticsExportDownloadStatePreviewInput,
): AnalyticsExportDownloadStatePreview {
  assertSafeAnalyticsExportDownloadStatePreview(input)

  const fileMetadata = input.file ?? {
    fileName: 'not_available',
    mimeType: 'not_available',
    rowCount: 0,
  }
  const payload: AnalyticsExportDownloadStatePreviewPayload = {
    downloadMode: input.downloadMode,
    fileName: fileMetadata.fileName,
    mimeType: fileMetadata.mimeType,
    rowCount: fileMetadata.rowCount,
    readyState: input.readyState,
    providerState: input.providerState,
    retentionPolicy: input.retentionPolicy,
  }

  return {
    mockSafe: true,
    payload,
    rows: [
      { field: 'downloadMode', value: getDownloadModeLabel(input.downloadMode) },
      { field: 'fileName', value: `${fileMetadata.fileName}（mock-safe 檔名預覽）` },
      { field: 'mimeType', value: fileMetadata.mimeType },
      { field: 'rowCount', value: String(fileMetadata.rowCount) },
      { field: 'readyState', value: getReadyStateLabel(input.readyState) },
      { field: 'providerState', value: getProviderStateLabel(input.providerState) },
      { field: 'retentionPolicy', value: getRetentionPolicyLabel(input.retentionPolicy) },
      {
        field: 'mock-safe 說明',
        value: 'download state preview，不查 D1，不建立 R2 檔案，不寫入匯出檔',
      },
    ],
  }
}

function getDownloadModeLabel(downloadMode: AnalyticsExportResponsePreviewDownloadMode) {
  if (downloadMode === 'browser_download') {
    return 'browser_download（mock-safe 前端下載，不建立 signed URL）'
  }
  return 'signed_url_pending（正式 signed URL 待實作）'
}

function getReadyStateLabel(readyState: AnalyticsExportDownloadStateReadyState) {
  if (readyState === 'ready_for_browser_download') {
    return 'ready_for_browser_download（前端已可下載）'
  }
  if (readyState === 'signed_url_pending') {
    return 'signed_url_pending（正式 signed URL 待實作）'
  }
  return 'not_ready（目前沒有可下載檔案）'
}

function getProviderStateLabel(providerState: string) {
  if (providerState === 'provider_not_configured') {
    return 'provider_not_configured（未啟用正式 provider）'
  }
  return `${providerState}（mock-safe provider state）`
}

function getRetentionPolicyLabel(retentionPolicy: AnalyticsExportDownloadStateRetentionPolicy) {
  if (retentionPolicy === 'pending_export_job_retention') {
    return 'pending_export_job_retention（正式 export job 保存期限待定）'
  }
  return 'not_persisted_mock_safe（不保存下載檔或 signed URL）'
}

function assertSafeAnalyticsExportDownloadStatePreview(value: unknown, path = 'input') {
  if (Array.isArray(value)) {
    value.forEach((item, index) =>
      assertSafeAnalyticsExportDownloadStatePreview(item, `${path}[${index}]`),
    )
    return
  }

  if (value && value instanceof Date) return

  if (value && typeof value === 'object') {
    for (const [key, nestedValue] of Object.entries(value)) {
      const normalizedKey = key.toLowerCase()
      if (forbiddenDownloadStatePreviewKeys.has(normalizedKey)) {
        throw new Error(
          `Unsafe analytics export download state preview: forbidden key ${path}.${key}`,
        )
      }
      assertSafeAnalyticsExportDownloadStatePreview(nestedValue, `${path}.${key}`)
    }
    return
  }

  if (typeof value !== 'string') return

  if (isUnsafeDownloadStatePreviewValue(value, path)) {
    throw new Error(`Unsafe analytics export download state preview: forbidden value at ${path}`)
  }
}

function isUnsafeDownloadStatePreviewValue(value: string, path: string) {
  const trimmedValue = value.trim()
  if (path.toLowerCase().endsWith('.filename')) {
    return (
      unsafeDownloadStateFileNameValuePatterns.some((pattern) => pattern.test(trimmedValue)) ||
      /^0\d{8,10}(?:#?\d+)?$/.test(trimmedValue) ||
      /^\d{8}$/.test(trimmedValue)
    )
  }

  return (
    unsafeDownloadStateValuePatterns.some((pattern) => pattern.test(trimmedValue)) ||
    /^\d{8}$/.test(trimmedValue)
  )
}
